import { useEffect, useState } from "react";
import { Barcode, CircleNotch, Warning, User } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { fmtBRL, fmtDate, todayISO, maskDocument } from "../lib/format";
import { ClientSearchInput } from "./ClientSearchInput";
import { DatePicker } from "./DatePicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { BoletoResultDialog, downloadBoletoPdf } from "./BoletoResultDialog";
import { BoletoGroupDialog } from "./BoletoGroupDialog";
import { AvulsoParcelasForm, emptyParc, parcelasPayload, parcelasProblem } from "./AvulsoParcelasForm";

export { downloadBoletoPdf };

const input = "w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500";
const label = "text-[10px] uppercase tracking-[0.15em] text-zinc-500 block mb-1";

const PagadorCard = ({ p, warnings }) => (
  <div className="border border-zinc-800 rounded-lg p-3 text-sm" data-testid="boleto-pagador-card">
    <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 flex items-center gap-1 mb-1.5"><User size={12} /> Pagador (dados do cadastro do cliente)</p>
    <p className="text-zinc-100 font-medium">{p.nome}</p>
    <p className="text-zinc-400 text-xs">{p.documento ? `CPF/CNPJ ${maskDocument(p.documento)}` : "CPF/CNPJ não informado"}{p.telefone ? ` · ${p.telefone}` : ""}</p>
    <p className="text-zinc-400 text-xs">{[p.endereco, p.bairro, p.cidade && `${p.cidade}/${p.uf}`, p.cep && `CEP ${p.cep}`].filter(Boolean).join(" — ") || "Endereço não informado"}</p>
    {warnings?.length > 0 && (
      <ul className="mt-2 space-y-1" data-testid="boleto-pagador-warnings">
        {warnings.map((w) => <li key={w} className="text-xs text-amber-300 flex items-start gap-1"><Warning size={13} className="mt-0.5 shrink-0" /> {w}</li>)}
      </ul>
    )}
  </div>
);

// mode: "entry" (target = lançamento) | "order" (target = pedido) | "avulso"
const REF_MAX = 10;
const refProblem = (v) => {
  const s = v.trim();
  if (!s) return "";
  if (s.length > REF_MAX) return `${s.length}/${REF_MAX} caracteres — o Sicredi aceita no máximo ${REF_MAX}`;
  if (!/^[A-Za-z0-9#\-./ ]+$/.test(s)) return "Use apenas letras, números, #, -, . e /";
  return "";
};

const ReferenciaField = ({ form, set, mode, preview, parcelado, nParc }) => {
  const v = form.seu_numero;
  const base = v.trim();
  const multi = (mode === "order" && preview?.multi) || (mode === "avulso" && parcelado);
  const exemplos = multi && base
    ? (mode === "order" ? preview.parcelas.filter((p) => p.will_emit).map((p) => `${base}-${p.installment_number}`) : Array.from({ length: Math.min(nParc || 2, 3) }, (_, i) => `${base}-${i + 1}`))
    : [];
  const problem = refProblem(v) || (exemplos.some((e) => e.length > REF_MAX) ? `Com o sufixo da parcela passa de ${REF_MAX} caracteres (ex.: ${exemplos.find((e) => e.length > REF_MAX)})` : "");
  return (
    <div data-testid="boleto-referencia-block">
      <label className={label}>Nº de referência no boleto (Seu Número / Nº do documento)</label>
      <input data-testid="boleto-referencia-input" className={`${input} ${problem ? "border-rose-500/60" : ""}`} maxLength={REF_MAX} value={v} onChange={(e) => set("seu_numero", e.target.value)}
        placeholder={mode === "avulso" ? "Ex.: NF 4521, ref. do cliente… (vazio = automático)" : "Ex.: #1330 ou NF 4521"} />
      <p data-testid="boleto-referencia-hint" className={`text-[11px] mt-1 ${problem ? "text-rose-300" : "text-zinc-500"}`}>
        {problem || (base
          ? (exemplos.length ? `Cada parcela recebe o sufixo: ${exemplos.join(", ")}${mode === "avulso" && nParc > 3 ? "…" : ""}` : `Vai impresso no boleto como "${base}"`)
          : mode === "avulso" ? "Em branco: o sistema usa o nº do lançamento (ex.: L361). Preencha com o nº da nota ou outra referência." : "Pré-preenchido com o nº do pedido — edite se preferir o nº da nota fiscal ou outra referência.")}
        <span className="text-zinc-600"> · {base.length}/{REF_MAX}</span>
      </p>
    </div>
  );
};

export const EmitBoletoDialog = ({ open, onClose, mode, target, clients = [], onDone }) => {
  const [dfl, setDfl] = useState(null);
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState({ multa_percent: "", juros_percent: "", juros_tipo: "DIARIO", instrucoes: "", client_id: "", amount: "", due_date: todayISO(), description: "", seu_numero: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [parcelado, setParcelado] = useState(false);
  const [parc, setParc] = useState(emptyParc());
  const [groupResult, setGroupResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setPreview(null);
    setParcelado(false);
    setParc(emptyParc());
    setForm((f) => ({ ...f, seu_numero: "" }));
    api.get("/boletos/defaults").then(({ data }) => {
      setDfl(data);
      setForm((f) => ({ ...f, multa_percent: String(data.multa_percent ?? ""), juros_percent: String(data.juros_percent ?? ""),
        juros_tipo: data.juros_tipo || "DIARIO", instrucoes: data.instrucoes || "" }));
    }).catch((e) => toast.error(apiError(e)));
    const loadPreview = (r) => { setPreview(r.data); setForm((f) => ({ ...f, seu_numero: r.data.seu_numero_sugerido || "" })); };
    if (mode === "entry" && target?.id) api.get(`/boletos/preview/entry/${target.id}`).then(loadPreview).catch((e) => toast.error(apiError(e)));
    if (mode === "order" && target?.id) api.get(`/boletos/preview/order/${target.id}`).then(loadPreview).catch((e) => toast.error(apiError(e)));
  }, [open, mode, target?.id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const refErr = refProblem(form.seu_numero);
    if (refErr) { toast.error(`Nº de referência: ${refErr}`); return; }
    setBusy(true);
    try {
      const opts = { multa_percent: Number(String(form.multa_percent).replace(",", ".")) || 0,
        juros_percent: Number(String(form.juros_percent).replace(",", ".")) || 0, juros_tipo: form.juros_tipo, instrucoes: form.instrucoes,
        seu_numero: form.seu_numero.trim() || null };
      let emitted;
      if (mode === "entry") {
        const { data } = await api.post(`/boletos/from-entry/${target.id}`, opts);
        emitted = [data];
        toast.success(`Boleto #${data.code} emitido${data.simulado ? " (simulação)" : ""}`);
      } else if (mode === "order") {
        const { data } = await api.post(`/boletos/from-order/${target.id}`, opts);
        emitted = data.emitted;
        if (data.emitted.length) toast.success(`${data.emitted.length} boleto(s) emitido(s) para o pedido #${data.order_code}`);
        if (data.skipped.length) toast.warning(`${data.skipped.length} parcela(s) ignorada(s): ${data.skipped.map((s) => `${s.parcela} — ${s.reason}`).join(" · ")}`, { duration: 9000 });
        if (data.emitted.length > 1) {
          onDone?.(data.emitted);
          setGroupResult({ orderId: target.id });
          return;
        }
      } else {
        if (!form.client_id) { toast.error("Selecione o cliente"); setBusy(false); return; }
        if (parcelado) {
          const problem = parcelasProblem(parc);
          if (problem) { toast.error(problem); setBusy(false); return; }
          const { data } = await api.post("/boletos/avulso/parcelado", { ...opts, client_id: form.client_id,
            total_amount: Number(String(parc.total).replace(",", ".")), description: form.description, parcelas: parcelasPayload(parc) });
          toast.success(`${data.emitted.length} boleto(s) emitido(s) — grupo #${data.group_code}${data.emitted[0]?.simulado ? " (simulação)" : ""}`);
          if (data.failed?.length) toast.warning(`${data.failed.length} parcela(s) não emitida(s): ${data.failed.map((f) => `${f.parcela} — ${f.reason}`).join(" · ")}`, { duration: 12000 });
          onDone?.(data.emitted);
          setGroupResult({ groupId: data.group_id });
          return;
        }
        const { data } = await api.post("/boletos/avulso", { ...opts, client_id: form.client_id,
          amount: Number(String(form.amount).replace(",", ".")), due_date: form.due_date, description: form.description });
        emitted = [data];
        toast.success(`Boleto avulso #${data.code} emitido${data.simulado ? " (simulação)" : ""}`);
      }
      onDone?.(emitted);
      if (emitted?.length) setResult(emitted[0]); else onClose();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const closeAll = () => { setResult(null); setGroupResult(null); onClose(); };
  const title = mode === "entry" ? `Emitir boleto — lançamento #${target?.code}` : mode === "order" ? `Emitir boletos — pedido #${target?.code}` : "Boleto avulso";
  const canEmit = mode === "entry" ? !!preview && preview.can_emit !== false : mode === "order" ? !!preview && preview.parcelas.some((p) => p.will_emit) : true;

  return (
    <>
      <Dialog open={open && !result && !groupResult} onOpenChange={(o) => !o && onClose()}>
        <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-lg max-h-[92vh] overflow-y-auto" data-testid="emit-boleto-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Barcode size={20} className="text-emerald-400" /> {title}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            {dfl && (
              <div data-testid="boleto-mode-banner" className={`text-xs rounded px-3 py-2 border ${dfl.modo === "simulacao" ? "text-amber-300 border-amber-500/30 bg-amber-500/10" : "text-emerald-300 border-emerald-500/30 bg-emerald-500/10"}`}>
                {dfl.modo === "simulacao"
                  ? "Modo SIMULAÇÃO: o boleto será gerado localmente, sem registro no Sicredi (sem valor para pagamento). Cadastre as credenciais em Segurança → Segredos para emitir de verdade."
                  : `Registro real no Sicredi — ambiente ${dfl.modo === "sandbox" ? "HOMOLOGAÇÃO" : "PRODUÇÃO"}.`}
              </div>
            )}

            {mode === "entry" && preview && (
              <>
                <div className="text-sm border border-zinc-800 rounded-lg p-3 grid grid-cols-3 gap-2" data-testid="boleto-parcela-card">
                  <div><span className={label}>Origem</span>{preview.entry.order_code ? `Pedido #${preview.entry.order_code} · parc. ${preview.entry.installment_number}/${preview.entry.installments_total}` : "Lançamento avulso"}</div>
                  <div><span className={label}>Vencimento</span>{fmtDate(preview.entry.due_date)}</div>
                  <div><span className={label}>Valor da parcela</span><b className="text-emerald-300">{fmtBRL(preview.entry.amount)}</b></div>
                </div>
                <PagadorCard p={preview.pagador} warnings={preview.warnings} />
                {preview.sicredi_payload && (
                  <details data-testid="boleto-payload-preview" className="border border-zinc-800 rounded-lg px-3 py-2 text-xs">
                    <summary className="cursor-pointer text-zinc-400">Ver JSON que será enviado ao Sicredi (com os padrões de multa/juros)</summary>
                    <pre data-testid="boleto-payload-json" className="mt-2 whitespace-pre-wrap break-all font-mono text-[11px] text-zinc-200 bg-zinc-950 rounded p-2 select-all">{JSON.stringify(preview.sicredi_payload, null, 2)}</pre>
                  </details>
                )}
                {preview.active_boleto && <p className="text-xs text-rose-300" data-testid="boleto-already-active">Esta parcela já tem o boleto #{preview.active_boleto.code} em carteira. Baixe-o antes de emitir outro.</p>}
              </>
            )}
            {mode === "order" && preview && (
              <>
                <PagadorCard p={preview.pagador} warnings={preview.warnings} />
                <div className="border border-zinc-800 rounded-lg overflow-hidden" data-testid="boleto-order-parcelas">
                  <table className="w-full text-xs">
                    <thead className="bg-zinc-900/60 text-[10px] uppercase tracking-[0.15em] text-zinc-500"><tr><th className="text-left px-3 py-2">Parcela</th><th className="text-left px-3 py-2">Vencimento</th><th className="text-right px-3 py-2">Valor</th><th className="text-left px-3 py-2">Situação</th></tr></thead>
                    <tbody className="divide-y divide-zinc-800">
                      {preview.parcelas.map((p) => (
                        <tr key={p.entry_id} className={p.will_emit ? "" : "opacity-50"}>
                          <td className="px-3 py-1.5 text-zinc-200">{p.is_entrada ? <span className="text-amber-300">Entrada</span> : p.parcela} <span className="text-zinc-500">#{p.entry_code}</span></td>
                          <td className="px-3 py-1.5 text-zinc-400">{fmtDate(p.due_date)}</td>
                          <td className="px-3 py-1.5 text-right text-zinc-200">{fmtBRL(p.amount)}</td>
                          <td className="px-3 py-1.5">{p.will_emit ? <span className="text-emerald-300">será emitido</span> : p.is_entrada ? <span className="text-zinc-400" data-testid={`boleto-entrada-skip-${p.entry_code}`}>entrada já paga ({p.payment_method}) — sem boleto</span> : p.status === "Pago" ? <span className="text-zinc-400">já paga</span> : <span className="text-sky-300">já tem boleto</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            {(mode === "entry" || mode === "order") && !preview && <p className="text-sm text-zinc-500">Carregando dados do cliente e da parcela...</p>}

            {mode === "avulso" && (
              <>
                <div>
                  <label className={label}>Cliente</label>
                  <ClientSearchInput clients={clients} value={form.client_id} onChange={(id) => set("client_id", id)} testId="avulso-client-input" />
                </div>
                <label className="flex items-start gap-2.5 cursor-pointer border border-zinc-800 rounded p-3 hover:border-zinc-700 transition-colors">
                  <input type="checkbox" data-testid="avulso-parcelado-checkbox" checked={parcelado} onChange={(e) => setParcelado(e.target.checked)} className="mt-0.5 accent-emerald-500 w-4 h-4" />
                  <span className="text-sm text-zinc-300">
                    Parcelado (dividir em várias parcelas)
                    <span className="block text-xs text-zinc-500 mt-0.5">Um boleto Sicredi para cada parcela — cada um com seu vencimento, nº de documento, nosso número, código de barras e Pix; todos vinculados ao mesmo grupo.</span>
                  </span>
                </label>
                {parcelado ? <AvulsoParcelasForm parc={parc} setParc={setParc} /> : (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={label}>Valor (R$)</label>
                      <input data-testid="avulso-amount-input" type="number" step="0.01" min="0.01" required className={input} value={form.amount} onChange={(e) => set("amount", e.target.value)} />
                    </div>
                    <div>
                      <label className={label}>Vencimento</label>
                      <DatePicker value={form.due_date} onChange={(v) => set("due_date", v)} testId="avulso-due-date" />
                    </div>
                  </div>
                )}
                <div>
                  <label className={label}>Descrição</label>
                  <input data-testid="avulso-description-input" className={input} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Ex.: Entrada do pedido / serviço avulso" />
                </div>
              </>
            )}
            <ReferenciaField form={form} set={set} mode={mode} preview={preview} parcelado={parcelado} nParc={parc?.parcelas?.length || 0} />
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className={label}>Multa após venc. (%)</label>
                <input data-testid="boleto-multa-input" type="number" step="0.01" min="0" max="20" className={input} value={form.multa_percent} onChange={(e) => set("multa_percent", e.target.value)} />
              </div>
              <div>
                <label className={label}>Mora / juros (%)</label>
                <input data-testid="boleto-juros-input" type="number" step="0.01" min="0" max="20" className={input} value={form.juros_percent} onChange={(e) => set("juros_percent", e.target.value)} />
              </div>
              <div>
                <label className={label}>Juros por</label>
                <select data-testid="boleto-juros-tipo-select" className={input} value={form.juros_tipo} onChange={(e) => set("juros_tipo", e.target.value)}>
                  <option value="DIARIO">Dia</option>
                  <option value="MENSAL">Mês</option>
                </select>
              </div>
            </div>
            <div>
              <label className={label}>Instruções adicionais (uma por linha)</label>
              <textarea data-testid="boleto-instrucoes-input" rows={3} className={input} value={form.instrucoes} onChange={(e) => set("instrucoes", e.target.value)} />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={onClose} className="text-sm text-zinc-400 hover:text-zinc-200 px-3 py-2">Cancelar</button>
              <button data-testid="emit-boleto-submit" type="submit" disabled={busy || !dfl || !canEmit}
                className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-60 text-white text-sm font-medium px-4 py-2 rounded transition-colors">
                {busy ? <CircleNotch size={15} className="animate-spin" /> : <Barcode size={15} />} Emitir boleto{mode === "order" || (mode === "avulso" && parcelado) ? "s" : ""}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      <BoletoResultDialog boleto={result} onClose={() => (groupResult ? setResult(null) : closeAll())} title="Boleto emitido" />
      <BoletoGroupDialog groupId={groupResult?.groupId} orderId={groupResult?.orderId} onClose={closeAll} onView={(b) => setResult(b)} onChanged={() => onDone?.([])} title="Boletos emitidos" />
    </>
  );
};
